// ./src/hooks/useEntitySearch.ts
//
// Debounced, parallel "universal search" for the ⌘K command palette (Phase 2).
// When the user stops typing (300 ms) and has typed ≥2 chars, it fan-outs the
// query to the existing `searchKeyword` list endpoints of Contacts, Event guests,
// and Events in parallel, and returns capped results grouped by entity. Stale
// responses are dropped via a monotonic call-id guard (same pattern as the entity
// list hooks). Callers gate each source by permission.

import { useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  Person,
  PersonService,
  Event,
  EventService,
  EventGuest,
  EventGuestService,
} from "@/utils/api";

export interface EntitySearchResults {
  contacts: Person[];
  guests: EventGuest[];
  events: Event[];
  /** A search is in flight for the current (debounced) query. */
  loading: boolean;
  /** The debounced query is long enough to have triggered a search. */
  active: boolean;
}

export interface UseEntitySearchOptions {
  /** Search contacts (needs the Audience permission). Default true. */
  contacts?: boolean;
  /** Search events + event guests (needs the Events permission). Default true. */
  events?: boolean;
  /** Max results per source. Default 6. */
  pageSize?: number;
}

const EMPTY: Omit<EntitySearchResults, "active"> = { contacts: [], guests: [], events: [], loading: false };

export const useEntitySearch = (query: string, opts?: UseEntitySearchOptions): EntitySearchResults => {
  const canContacts = opts?.contacts ?? true;
  const canEvents = opts?.events ?? true;
  const pageSize = opts?.pageSize ?? 6;

  const [debounced] = useDebouncedValue(query, 300);
  const q = debounced.trim();
  const active = q.length >= 2;

  const [state, setState] = useState<Omit<EntitySearchResults, "active">>(EMPTY);
  const callIdRef = useRef(0);

  useEffect(() => {
    if (!active) {
      callIdRef.current++; // cancel any in-flight response
      setState(EMPTY);
      return;
    }

    const callId = ++callIdRef.current;
    setState((s) => ({ ...s, loading: true }));

    const pagination = { page: 1, pageSize };
    const pick = <T,>(res: { code?: number; data?: unknown } | null): T[] =>
      res && res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) ? (res.data as T[]) : [];

    Promise.all([
      canContacts ? PersonService.list({ searchKeyword: q }, pagination) : Promise.resolve(null),
      canEvents ? EventGuestService.list({ searchKeyword: q }, pagination) : Promise.resolve(null),
      canEvents ? EventService.list({ searchKeyword: q }, pagination) : Promise.resolve(null),
    ]).then(([rc, rg, re]) => {
      if (callId !== callIdRef.current) return;
      setState({
        contacts: pick<Person>(rc),
        guests: pick<EventGuest>(rg),
        events: pick<Event>(re),
        loading: false,
      });
    });
    // pageSize is stable; q/active/permissions drive re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, active, canContacts, canEvents]);

  return { ...state, active };
};
